import { createRoute, redirect, Link } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import type {
  BusinessAlertDto,
  BusinessAlertKindDto,
  BusinessAlertTargetDto,
} from "@carry/contract";
import { Route as RootRoute } from "./__root";
import { useAdminApi } from "../providers/api-context";
import { formatDateTime } from "../lib/format";

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/alerts",
  beforeLoad: ({ context }) => {
    if (context.authLoading) return;
    if (!context.session) throw redirect({ to: "/auth/login" });
  },
  component: AlertsPage,
});

const ALERT_LABEL: Record<BusinessAlertKindDto, string> = {
  distance_deviation_alert: "距離乖離",
  unconfirmed_instruction: "配送指示未確認",
  unsubmitted_report: "日報未提出",
  alcohol_check_failed: "アルコール検査 FAIL",
  vehicle_inspection_ng: "車両点検 NG",
  license_expiration_alert: "免許有効期限警告",
  daily_report_rejected: "日報差戻し",
  delay_reported: "遅延報告",
  unrecorded_arrival_odometer: "到着ODO未入力",
};

const ALERT_INK: Record<BusinessAlertKindDto, string> = {
  distance_deviation_alert: "var(--warn)",
  unconfirmed_instruction: "var(--warn)",
  unsubmitted_report: "var(--warn)",
  alcohol_check_failed: "var(--danger)",
  vehicle_inspection_ng: "var(--danger)",
  license_expiration_alert: "var(--warn)",
  daily_report_rejected: "var(--warn)",
  delay_reported: "var(--warn)",
  unrecorded_arrival_odometer: "var(--warn)",
};

function AlertsPage() {
  const api = useAdminApi();
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchNextPageError,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["alerts"],
    queryFn: ({ pageParam }) =>
      api.alert.list(pageParam ? { limit: 20, cursor: pageParam } : { limit: 20 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_cursor ?? undefined,
    refetchInterval: 30_000,
  });

  const alerts = deduplicateAlerts(data?.pages.flatMap((page) => page.data) ?? []);

  return (
    <div style={{ margin: -24 }}>
      <div
        style={{
          padding: "12px 32px",
          display: "flex",
          alignItems: "center",
          gap: 16,
          borderBottom: "0.5px solid var(--rule-softer)",
          background: "var(--bg)",
        }}
      >
        <Link
          to="/"
          style={{
            fontSize: 12,
            color: "var(--accent)",
            textDecoration: "none",
            padding: "5px 10px",
            border: "0.5px solid var(--rule-soft)",
            borderRadius: 2,
            background: "var(--bg-raised)",
          }}
        >
          ← ダッシュボード
        </Link>
      </div>

      <div style={{ padding: "24px 32px 32px" }}>
        {isLoading && <div style={{ fontSize: 12, color: "var(--ink-3)" }}>読み込み中…</div>}
        {isError && alerts.length === 0 && (
          <ErrorPanel
            message="アラートの取得に失敗しました"
            detail={error instanceof Error ? error.message : undefined}
            buttonLabel="再試行"
            onRetry={() => void refetch()}
          />
        )}
        {!isLoading && !isError && alerts.length === 0 && (
          <div
            style={{ fontSize: 13, color: "var(--ink-3)", textAlign: "center", padding: "48px 0" }}
          >
            アラート履歴はありません
          </div>
        )}
        {alerts.length > 0 && (
          <>
            <div
              style={{
                background: "var(--bg-raised)",
                border: "0.5px solid var(--rule)",
                borderRadius: 3,
              }}
            >
              {alerts.map((alert, index) => (
                <AlertRow key={alert.id} alert={alert} isLast={index === alerts.length - 1} />
              ))}
            </div>

            {isFetchNextPageError && (
              <ErrorPanel
                message="追加のアラート取得に失敗しました"
                buttonLabel="追加読み込みを再試行"
                onRetry={() => void fetchNextPage()}
              />
            )}
            {hasNextPage && !isFetchNextPageError && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <button
                  type="button"
                  onClick={() => void fetchNextPage()}
                  disabled={isFetchingNextPage}
                  style={loadMoreButtonStyle}
                >
                  {isFetchingNextPage ? "読み込み中…" : "さらに読み込む"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function deduplicateAlerts(alerts: BusinessAlertDto[]) {
  const seen = new Set<string>();
  return alerts.filter((alert) => {
    if (seen.has(alert.id)) return false;
    seen.add(alert.id);
    return true;
  });
}

function AlertRow({ alert, isLast }: { alert: BusinessAlertDto; isLast: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        padding: "14px 18px",
        borderBottom: isLast ? "none" : "0.5px solid var(--rule-soft)",
        alignItems: "flex-start",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: ALERT_INK[alert.kind],
          flexShrink: 0,
          marginTop: 5,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 4,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: ALERT_INK[alert.kind],
              border: "0.5px solid var(--rule-soft)",
              borderRadius: 2,
              padding: "1px 5px",
            }}
          >
            種別: {ALERT_LABEL[alert.kind]}
          </span>
          <span style={{ fontSize: 10, fontWeight: 600, color: "var(--ink-2)" }}>
            {alert.status === "active" ? "未解消" : "解消済み"}
          </span>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{alert.title}</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-2)" }}>{alert.body}</div>
        <TargetLink target={alert.target} />
      </div>
      <div
        style={{
          display: "grid",
          gap: 3,
          fontSize: 11,
          color: "var(--ink-3)",
          whiteSpace: "nowrap",
          textAlign: "right",
        }}
      >
        <span>発生 {formatDateTime(alert.occurred_at)}</span>
        <span>解消 {alert.resolved_at ? formatDateTime(alert.resolved_at) : "—"}</span>
      </div>
    </div>
  );
}

function TargetLink({ target }: { target: BusinessAlertTargetDto }) {
  if (!target) return null;

  const style = {
    display: "inline-block",
    marginTop: 6,
    fontSize: 11,
    color: "var(--accent)",
    textDecoration: "none",
  } as const;

  switch (target.kind) {
    case "distance":
      // #627: 距離突合を廃止したため、走行距離乖離は運転日報で確認する。
      return (
        <Link to="/reports" style={style}>
          対象の運転日報を開く
        </Link>
      );
    case "route":
      return (
        <Link to="/routes" search={{ date: target.date }} style={style}>
          対象日の配送指示を開く
        </Link>
      );
    case "report":
      return (
        <Link to="/reports" style={style}>
          対象の日報を開く
        </Link>
      );
    case "order":
      return (
        <Link
          to="/orders/$publicId"
          params={{ publicId: target.order_public_id }}
          aria-label={`対象注文 ${target.order_number ?? target.order_public_id} を開く`}
          style={style}
        >
          対象注文: {target.order_number ?? target.order_public_id}
        </Link>
      );
  }
}

function ErrorPanel({
  message,
  detail,
  buttonLabel,
  onRetry,
}: {
  message: string;
  detail?: string;
  buttonLabel: string;
  onRetry: () => void;
}) {
  return (
    <div role="alert" style={{ padding: "18px", textAlign: "center", color: "var(--danger)" }}>
      <div style={{ fontSize: 12 }}>{message}</div>
      {detail && <div style={{ marginTop: 4, fontSize: 11, color: "var(--ink-3)" }}>{detail}</div>}
      <button type="button" onClick={onRetry} style={{ ...loadMoreButtonStyle, marginTop: 10 }}>
        {buttonLabel}
      </button>
    </div>
  );
}

const loadMoreButtonStyle = {
  fontSize: 11,
  fontWeight: 500,
  padding: "6px 12px",
  border: "0.5px solid var(--rule-soft)",
  borderRadius: 2,
  background: "var(--bg-raised)",
  color: "var(--ink)",
  cursor: "pointer",
} as const;
